import axios from 'axios';

/**
 * Resolves a Google Places autocomplete prediction into address fields.
 *
 * The old approach read the prediction's `terms` array by fixed index
 * (terms[2] = city, terms[3] = state, terms[4]/[5] = zip). That only holds for
 * a bare "number street, city, state, country" prediction. Any prediction
 * carrying an extra leading component — a subpremise (suite/unit) or a business
 * name — shifts every later term by one, so the street name landed in `city`
 * and the city landed in `state`.
 *
 * The mapping now lives on the backend (`GET /places/details/:placeId`, see
 * src/utils/addressComponents.js there) so the provider web app and the mobile
 * app share one implementation. This module only translates the response's
 * `locations`-column names onto this form's field names.
 */

/**
 * Fetches Place Details for a prediction and returns the backend's mapped
 * address fields, renamed for the location form.
 * Throws on network/API failure so callers can fall back.
 */
export const fetchPlaceDetails = async (placeId, token, sessionToken) => {
    if (!placeId) {
        throw new Error('placeId is required');
    }

    const serverUrl = process.env.REACT_APP_SERVER_URL;

    const response = await axios.get(`${serverUrl}/places/details/${encodeURIComponent(placeId)}`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        // Closes the billing session opened by the autocomplete calls.
        params: sessionToken ? { sessiontoken: sessionToken } : {},
    });

    const address = response.data?.address;

    if (!address) {
        throw new Error('Place details response missing address');
    }

    return {
        location_address: address.address || '',
        city: address.city || '',
        state: address.state || '',
        zipCode: address.zip || '',
        suite: address.suite || '',
    };
};

/**
 * Last-resort parse of a prediction when Place Details is unavailable.
 *
 * Works backwards from the end of `terms`, where the ordering is stable
 * (… city, state, [zip], [country]), rather than forwards from the start,
 * where a subpremise or business name shifts everything. Deliberately leaves
 * `suite` alone: it is not recoverable from `terms` without guessing.
 */
export const parsePredictionTerms = (prediction) => {
    const terms = (prediction?.terms || []).map((term) => term?.value || '').filter(Boolean);

    // Drop a trailing country term so `state` is always the last-but-one entry.
    const withoutCountry = terms.length && /^(USA|United States)$/i.test(terms[terms.length - 1])
        ? terms.slice(0, -1)
        : terms;

    let zipCode = '';
    let rest = withoutCountry;

    // A trailing "CA 92009" or standalone "92009" carries the zip.
    const last = rest[rest.length - 1] || '';
    const zipMatch = last.match(/\b(\d{5})(?:-\d{4})?$/);
    if (zipMatch) {
        zipCode = zipMatch[1];
        const remainder = last.slice(0, zipMatch.index).trim();
        rest = remainder ? [...rest.slice(0, -1), remainder] : rest.slice(0, -1);
    }

    if (!zipCode && prediction?.description) {
        const descriptionZip = prediction.description.match(/\b\d{5}(?:-\d{4})?\b/);
        if (descriptionZip) {
            zipCode = descriptionZip[0].slice(0, 5);
        }
    }

    const state = rest.length >= 2 ? rest[rest.length - 1] : '';
    const city = rest.length >= 3 ? rest[rest.length - 2] : '';

    return {
        location_address: prediction?.structured_formatting?.main_text || '',
        city,
        // Only trust a genuine 2-letter code here; anything longer means the
        // prediction was not shaped as expected and we would be writing a city
        // name into `state` all over again.
        state: /^[A-Za-z]{2}$/.test(state) ? state.toUpperCase() : '',
        zipCode,
    };
};
